const HTML_ENTITY_MAP = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
} as const;

/**
 * Decodes HTML entities using a pure string map (no DOM dependency).
 * Handles named entities from the map and numeric/hex entities (&#123; / &#x1F600;).
 */
export function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&(?:#x([0-9a-fA-F]+)|#(\d+)|[a-zA-Z]+);/g, (match, hex, dec) => {
      if (hex) {
        const cp = parseInt(hex, 16);
        return cp <= 0x10FFFF ? String.fromCodePoint(cp) : match;
      }
      if (dec) {
        const cp = parseInt(dec, 10);
        return cp <= 0x10FFFF ? String.fromCodePoint(cp) : match;
      }
      return (HTML_ENTITY_MAP as Record<string, string>)[match] ?? match;
    });
}

/**
 * Strips HTML tags using DOMParser (robust against malformed HTML).
 */
export function stripHtmlTags(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent ?? '';
}

export function randomString(length = 6) {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }
  return result;
}

/**
 * Joins a first and last name into a display name, dropping a missing last
 * name instead of stringifying it. `${first} ${last}` rendered "Damian null"
 * on the dashboard and sidebar when the account had no last name (the value
 * arrives as null from the backend). (AGI-364)
 */
export function composeFullName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  return [firstName, lastName]
    .filter((part): part is string => Boolean(part))
    .join(' ');
}

export function capitalizeWord(text: string = '') {
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Truncates a filename if it exceeds the maximum length
 * @param filename - The original filename to truncate
 * @param maxLength - Maximum allowed length before truncation
 * @param outputLength - Length of the output string after truncation
 * @returns The truncated filename or original if not exceeding max length
 */
export function truncateName(
  filename: string,
  maxLength: number = 20,
  outputLength: number = 15,
): string {
  if (filename.length <= maxLength) {
    return filename;
  }

  const extension = filename.includes('.')
    ? filename.slice(filename.lastIndexOf('.'))
    : '';
  const name = filename.slice(0, filename.length - extension.length);

  const truncatedLength = outputLength - extension.length - 3; // Account for '...' in the middle
  const start = Math.ceil(truncatedLength / 2);
  const end = Math.floor(truncatedLength / 2);

  return `${name.slice(0, start)}...${name.slice(name.length - end)}${extension}`;
}
